import { type ReactNode, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePageTranslation } from "@/i18n/useTranslation";
import { supportTranslations as enSupport } from "@/i18n/locales/en/support";
import { supportTranslations as thSupport } from "@/i18n/locales/th/support";
import { createTicket } from "./api/supportApi";
import { useTicketPageContext } from "./widget/useTicketPageContext";
import { RichComposer } from "./components/RichComposer";
import { KnowledgeBaseSuggestions } from "./components/KnowledgeBaseSuggestions";
import { CategoryOption, PriorityOption, categoryLabel, priorityLabel } from "./components/meta";
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  type TicketCategory,
  type TicketPriority,
} from "./types";

export function SupportComposePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = usePageTranslation({ en: enSupport, th: thSupport }) as typeof enSupport;

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState<TicketCategory>("Technical");
  const [priority, setPriority] = useState<TicketPriority>("Medium");
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyText, setBodyText] = useState("");
  // Capture session/user context so support can jump to the PostHog replay later.
  const pageContext = useTicketPageContext();

  const mutation = useMutation({
    mutationFn: () =>
      createTicket({ subject, category, priority, bodyHtml, body: bodyText, context: pageContext }),
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ["support", "tickets"] });
      navigate(`/support/${ticket.name}`, { replace: true });
    },
  });

  const canSubmit =
    subject.trim().length > 2 && bodyText.trim().length > 2 && !mutation.isPending;

  const close = () => {
    if (!mutation.isPending) navigate("/support");
  };

  return (
    <PageContainer>
      {/* Back */}
      <button
        onClick={close}
        disabled={mutation.isPending}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-100"
      >
        <ArrowLeft className="h-4 w-4" />
        {t.backToSupport}
      </button>

      <div className="mx-auto mt-4 max-w-2xl">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t.composeTitle}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t.composeSubtitle}</p>
        </div>

        {/* Form */}
        <div className="mt-6 space-y-6">
          {/* Subject */}
          <div className="space-y-2">
            <FieldLabel htmlFor="subject">{t.subjectLabel}</FieldLabel>
            <Input
              id="subject"
              placeholder={t.subjectPlaceholder}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              autoFocus
              className="bg-white dark:bg-gray-900/40"
            />
            <KnowledgeBaseSuggestions query={subject} />
          </div>

          {/* Category + Priority — grouped settings rows */}
          <div className="space-y-2">
            <FieldLabel>{t.detailsLabel}</FieldLabel>
            <div className="divide-y divide-border/40 overflow-hidden rounded-xl border border-border/40 bg-white dark:bg-gray-900/40">
              <FieldRow label={t.categoryLabel}>
                <Select value={category} onValueChange={(v) => setCategory(v as TicketCategory)}>
                  <SelectTrigger className="min-w-[9rem] justify-end">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        <CategoryOption category={c} label={categoryLabel(t, c)} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label={t.priorityLabel}>
                <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                  <SelectTrigger className="min-w-[9rem] justify-end">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>
                        <PriorityOption priority={p} label={priorityLabel(t, p)} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <FieldLabel>{t.descriptionLabel}</FieldLabel>
            <RichComposer
              placeholder={t.descriptionPlaceholder}
              onChange={(html, text) => {
                setBodyHtml(html);
                setBodyText(text);
              }}
            />
            <p className="text-xs text-gray-400 dark:text-gray-500">{t.descriptionTip}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            className="rounded-full"
            onClick={close}
            disabled={mutation.isPending}
          >
            {t.cancel}
          </Button>
          <Button
            className="rounded-full"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {t.sendRequest}
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}

function FieldLabel({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <Label
      htmlFor={htmlFor}
      className="px-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
    >
      {children}
    </Label>
  );
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
